import React, { useState, useEffect, useContext, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { User } from '../models/User';
import { getMatches } from '../api/userApi';
import { FavoriteUserCard } from './FavoriteUserCard';
import { EmptyMatches } from './EmptyMatches';
import { AppContext } from '../window';
import { useTranslation } from '../i18n';

interface MatchesProps {
  title?: string;
  className?: string;
  showSearch?: boolean;
}

export const Matches: React.FC<MatchesProps> = ({
  title,
  className = '',
  showSearch = true
}) => {
  const [matches, setMatches] = useState<User[]>([]);
  const [filteredMatches, setFilteredMatches] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  
  const { texts } = useContext(AppContext);
  const { t } = useTranslation();

  // Load matches from API
  const loadMatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getMatches();
      if (response.error) {
        setError(response.error);
      } else {
        setMatches(response.users);
        setFilteredMatches(response.users);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('Error loading matches:', err);
      setError(t('matches.failed_to_load'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  // Filter matches based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMatches(matches);
    } else {
      const filtered = matches.filter(user =>
        user.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.town?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.province?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMatches(filtered);
    }
  }, [searchTerm, matches]);

  // Handle when a user is unfavorited - in matches, we don't remove them from the list
  const handleRemoveUser = useCallback(() => {
    // Unlike in Favorites.tsx, we don't remove the user from matches
    // when they are unfavorited. Matches should remain visible.
    // The notification is already shown by FavoriteButton component
  }, []);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  if (loading) {
    return (
      <Container className={`matches-list ${className}`}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('matches.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className={`matches-list ${className}`}>
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span>{error}</span>
          <Button variant="outline-danger" size="sm" onClick={loadMatches}>
            {t('matches.retry')}
          </Button>
        </Alert>
      </Container>
    );
  }

  if (matches.length === 0) {
    return (
      <Container className={`matches-list ${className}`}>
        <EmptyMatches />
      </Container>
    );
  }

  return (
    <Container className={`matches-list pt-2 ${className}`}>
      {/* Search */}
      {showSearch && matches.length > 0 && (
        <Row className="mb-2">
          <Col md={6} lg={4}>
            <InputGroup>
              <InputGroup.Text>
                <SearchIcon fontSize="small" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('matches.search_matches')}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={handleClearSearch}
                  title={t('matches.clear_search')}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>
      )}

      {/* Results info */}
      {searchTerm && (
        <Row className="mb-3">
          <Col>
            <Alert variant="info" className="mb-0">
              {filteredMatches.length === 0 
                ? t('matches.no_search_results', { search: searchTerm })
                : t('matches.search_results', { count: filteredMatches.length, search: searchTerm })}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Matches Grid */}
      {filteredMatches.length > 0 ? (
        <>
          <Row className="g-3 mb-4">
            {filteredMatches.map((user) => (
              <Col key={user.id} xs={12} md={4} lg={3}>
                <FavoriteUserCard 
                  user={user} 
                  onRemove={handleRemoveUser}
                  showRemovingState={false}
                />
              </Col>
            ))}
          </Row>
        </>
      ) : searchTerm ? (
        <EmptyMatches />
      ) : null}
    </Container>
  );
};